import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileText, Table, PenLine, Check, ArrowRight } from "lucide-react";
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  Button,
  Pill,
  AlertDialog,
  Skeleton,
  Field,
  Input,
  Select,
  Checkbox,
} from "@/design/components";
import { formatHumanCurrency } from "@/design/format";
import { useSeed, useMutate, useActorId, useActiveProfile } from "@/domain/hooks";
import { createSpendFromDocument, importSpendFromCsv } from "@/domain/mutations";
import { useRaiseSpend } from "@/domain/useMutations";
import { SPEND_CATEGORIES, type SpendCategory } from "@/domain/types";

type Method = "manual" | "document" | "csv";

const METHODS: { value: Method; label: string; icon: typeof PenLine; hint: string }[] = [
  { value: "manual", label: "Manual entry", icon: PenLine, hint: "Type a single line of spend." },
  { value: "document", label: "Document upload", icon: FileText, hint: "Drop an invoice; we pre-fill the draft." },
  { value: "csv", label: "Statement import", icon: Table, hint: "Reconcile a mobile-money statement." },
];

export function RecordSpendDrawer({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const seed = useSeed();
  const mutate = useMutate();
  const actorId = useActorId();
  const profile = useActiveProfile();
  const { raise } = useRaiseSpend();
  const [method, setMethod] = useState<Method>("manual");
  const [manualDirty, setManualDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const subs = seed.subProjects.filter((s) => s.projectId === projectId);
  const ctx = { reporterId: actorId, organisationId: profile.organisationId, projectId };

  function close() {
    onOpenChange(false);
    setMethod("manual");
    setManualDirty(false);
  }

  async function handleManualSubmit(input: { title: string; amount: number; category: SpendCategory; supplier?: string; subProjectId?: string; note?: string }) {
    await raise({ ...ctx, ...input, purpose: input.note || "Recorded by the contractor lead." });
    toast.success("Spend ask recorded and sent for approval");
    close();
  }

  // Guard a typed manual-entry draft (title / amount / note) from being lost on
  // Escape, backdrop, or the Close button.
  function requestClose() {
    if (method === "manual" && manualDirty) {
      setConfirmDiscard(true);
      return;
    }
    close();
  }

  return (
    <Drawer open={open} onOpenChange={(o) => !o && requestClose()} width="640px">
      <DrawerHeader>
        <DrawerTitle className="text-base font-semibold text-ink">Record spend</DrawerTitle>
        <p className="mt-0.5 text-sm text-mute">Capture spend three ways. Each method reconciles against this project's budget by category.</p>
      </DrawerHeader>

      <DrawerBody className="space-y-5">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {METHODS.map((m) => (
            <button
              key={m.value}
              onClick={() => setMethod(m.value)}
              aria-pressed={method === m.value}
              className={`rounded-lg border p-3 text-left transition-colors ${method === m.value ? "border-brand bg-brandSoft" : "border-line hover:bg-subtle"}`}
            >
              <m.icon className={`h-4 w-4 ${method === m.value ? "text-brand" : "text-faint"}`} />
              <p className="mt-1.5 text-sm font-medium text-ink">{m.label}</p>
              <p className="text-[11px] leading-snug text-faint">{m.hint}</p>
            </button>
          ))}
        </div>

        {method === "manual" && <ManualEntry subs={subs} onDirtyChange={setManualDirty} onSubmit={handleManualSubmit} />}
        {method === "document" && <DocumentUpload subs={subs} onCommit={(input) => { mutate((s) => { const res = createSpendFromDocument(s, { ...ctx, ...input }); const r = res.seed.spendRequests.find((x) => x.id === res.id); if (r) r.state = "confirmed"; return res.seed; }); toast.success("Invoice reconciled and recorded as confirmed spend"); close(); }} />}
        {method === "csv" && <CsvImport onImport={(rows, subProjectId) => { mutate((s) => importSpendFromCsv(s, rows, { ...ctx, subProjectId })); toast.success(`${rows.length} statement rows reconciled and imported`); close(); }} subs={subs} />}
      </DrawerBody>

      <DrawerFooter>
        <Button variant="ghost" onClick={requestClose}>Close</Button>
      </DrawerFooter>

      <AlertDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Discard your note?"
        description="This manual spend entry has not been recorded. If you close now the description, amount and note you typed will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        tone="danger"
        onConfirm={close}
      />
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Method 1: manual entry
// ---------------------------------------------------------------------------

function ManualEntry({ subs, onSubmit, onDirtyChange }: { subs: { id: string; name: string }[]; onSubmit: (i: { title: string; amount: number; category: SpendCategory; supplier?: string; subProjectId?: string; note?: string }) => void; onDirtyChange: (dirty: boolean) => void }) {
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState<SpendCategory>("Materials");
  const [supplier, setSupplier] = useState("");
  const [subProjectId, setSubProjectId] = useState("");
  const [note, setNote] = useState("");
  const amountNum = Number(amount.replace(/[^0-9]/g, ""));
  const valid = title.trim() && amountNum > 0;
  // Report any typed-in content up so the drawer can guard against losing it on close.
  const dirty = Boolean(title.trim() || amount.trim() || supplier.trim() || subProjectId || note.trim());
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  return (
    <div className="space-y-3">
      <Field label="Description"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What was bought" /></Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Category"><Select value={category} onChange={(e) => setCategory(e.target.value as SpendCategory)}>{SPEND_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</Select></Field>
        <Field label="Amount (UGX)"><Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="numeric" placeholder="5,000,000" />{amountNum > 0 && <p className="mt-1 text-xs text-faint">{formatHumanCurrency(amountNum)}</p>}</Field>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Supplier"><Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Supplier name" /></Field>
        <Field label="Sub-project"><Select value={subProjectId} onChange={(e) => setSubProjectId(e.target.value)}><option value="">Select</option>{subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
      </div>
      <Field label="Note (optional)"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything to add" /></Field>
      <Button className="w-full" disabled={!valid} onClick={() => onSubmit({ title: title.trim(), amount: amountNum, category, supplier: supplier.trim() || undefined, subProjectId: subProjectId || undefined, note: note.trim() || undefined })}>
        Record spend
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Method 2: document upload (mock extraction)
// ---------------------------------------------------------------------------

function DocumentUpload({ subs, onCommit }: { subs: { id: string; name: string }[]; onCommit: (i: { title: string; amount: number; category: SpendCategory; supplier?: string; subProjectId?: string }) => void }) {
  const [stage, setStage] = useState<"drop" | "extracting" | "review">("drop");
  const [confirmRecord, setConfirmRecord] = useState(false);
  // Pre-filled draft from a faked extraction.
  const [draft, setDraft] = useState({ title: "Distribution boards · invoice INV-5521", amount: "11200000", category: "Equipment and plant" as SpendCategory, supplier: "East African Wire & Cable Co.", subProjectId: "" });
  const amountNum = Number(draft.amount.replace(/[^0-9]/g, ""));

  function fakeUpload() {
    setStage("extracting");
    setTimeout(() => setStage("review"), 900);
  }

  if (stage === "drop") {
    return (
      <button onClick={fakeUpload} className="flex min-h-44 w-full flex-col items-center justify-center rounded-card border-2 border-dashed border-line transition-colors hover:border-brand">
        <UploadCloud className="h-7 w-7 text-faint" />
        <p className="mt-2 text-sm font-medium text-ink">Drop an invoice or receipt</p>
        <p className="text-xs text-faint">PDF, JPG or PNG · we extract the supplier, amount and category</p>
      </button>
    );
  }
  if (stage === "extracting") {
    return (
      <div className="space-y-3 rounded-card border border-line p-4" aria-busy="true">
        <p className="text-sm text-mute">Reading the document and extracting the supplier, amount and category…</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2"><Skeleton className="h-3 w-1/3" /><Skeleton className="h-9 w-full" /></div>
          <div className="space-y-2"><Skeleton className="h-3 w-1/3" /><Skeleton className="h-9 w-full" /></div>
          <div className="space-y-2"><Skeleton className="h-3 w-1/3" /><Skeleton className="h-9 w-full" /></div>
          <div className="space-y-2"><Skeleton className="h-3 w-1/3" /><Skeleton className="h-9 w-full" /></div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 rounded-md bg-okSoft px-3 py-2 text-sm text-ok"><Check className="h-4 w-4" /> Extracted from invoice-INV-5521.pdf. Review before recording.</div>
      <Field label="Description"><Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Category"><Select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value as SpendCategory })}>{SPEND_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</Select></Field>
        <Field label="Amount (UGX)"><Input value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} /><p className="mt-1 text-xs text-faint">{formatHumanCurrency(amountNum)}</p></Field>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Supplier"><Input value={draft.supplier} onChange={(e) => setDraft({ ...draft, supplier: e.target.value })} /></Field>
        <Field label="Sub-project"><Select value={draft.subProjectId} onChange={(e) => setDraft({ ...draft, subProjectId: e.target.value })}><option value="">Select</option>{subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
      </div>
      <Button className="w-full" disabled={!amountNum} onClick={() => setConfirmRecord(true)}>
        Record as confirmed spend
      </Button>
      <AlertDialog
        open={confirmRecord}
        onOpenChange={setConfirmRecord}
        title="Record as confirmed spend?"
        description={`This records ${formatHumanCurrency(amountNum)} for '${draft.title.trim()}' as confirmed, reconciled spend against this project's budget. It skips the approval queue, so check the extracted figures first.`}
        confirmLabel={`Record ${formatHumanCurrency(amountNum)}`}
        onConfirm={() => onCommit({ title: draft.title.trim(), amount: amountNum, category: draft.category, supplier: draft.supplier.trim() || undefined, subProjectId: draft.subProjectId || undefined })}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Method 3: CSV / mobile-money statement reconcile (4 steps)
// ---------------------------------------------------------------------------

const SAMPLE_ROWS: { date: string; description: string; amount: number; category: SpendCategory; supplier: string }[] = [
  { date: "2026-06-02", description: "Cement delivery · 80 bags", amount: 3_200_000, category: "Materials", supplier: "Kabale Cement Group" },
  { date: "2026-06-03", description: "Scaffold hire · week 22", amount: 1_700_000, category: "Equipment and plant", supplier: "Lakeland Builders Merchants" },
  { date: "2026-06-04", description: "Site labour · gang of 6", amount: 2_400_000, category: "Labour", supplier: "EAFE direct labour" },
  { date: "2026-06-05", description: "Diesel for generator", amount: 640_000, category: "Logistics and transport", supplier: "Total Energies Entebbe" },
];

function CsvImport({ subs, onImport }: { subs: { id: string; name: string }[]; onImport: (rows: typeof SAMPLE_ROWS, subProjectId?: string) => void }) {
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState<number[]>(SAMPLE_ROWS.map((_, i) => i));
  const [subProjectId, setSubProjectId] = useState("");
  const [confirmImport, setConfirmImport] = useState(false);
  const STEPS = ["Upload", "Map columns", "Select", "Import"];
  const rows = selected.map((i) => SAMPLE_ROWS[i]);
  const rowsTotal = rows.reduce((a, r) => a + r.amount, 0);

  return (
    <div className="space-y-4">
      <ol className="flex items-center gap-1 text-xs">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-1">
            <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold ${i <= step ? "bg-brand text-white" : "bg-subtle text-faint"}`}>{i + 1}</span>
            <span className={i === step ? "font-medium text-ink" : "text-faint"}>{s}</span>
            {i < STEPS.length - 1 && <span className="mx-1 h-px w-4 bg-line" />}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <button onClick={() => setStep(1)} className="flex min-h-40 w-full flex-col items-center justify-center rounded-card border-2 border-dashed border-line transition-colors hover:border-brand">
          <UploadCloud className="h-7 w-7 text-faint" />
          <p className="mt-2 text-sm font-medium text-ink">Upload a bank or mobile-money statement</p>
          <p className="text-xs text-faint">CSV · we detect the columns and match them to spend</p>
        </button>
      )}

      {step === 1 && (
        <div className="space-y-2">
          <p className="text-sm text-mute">We detected these columns. Confirm the mapping to Impact Ledger fields.</p>
          {[["Txn date", "Date"], ["Narration", "Description"], ["Debit (UGX)", "Amount"], ["Counterparty", "Supplier"]].map(([from, to]) => (
            <div key={from} className="flex items-center justify-between rounded-md border border-line px-3 py-2 text-sm">
              <span className="text-mute">{from}</span>
              <ArrowRight className="h-3.5 w-3.5 text-faint" />
              <span className="font-medium text-ink">{to}</span>
            </div>
          ))}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-2">
          <p className="text-sm text-mute">Select the rows to reconcile into this project.</p>
          <ul className="divide-y divide-hairline rounded-md border border-line">
            {SAMPLE_ROWS.map((r, i) => (
              <li key={i} className="flex items-center gap-3 px-3 py-2">
                <Checkbox checked={selected.includes(i)} onChange={(e) => setSelected((xs) => e.target.checked ? [...xs, i] : xs.filter((x) => x !== i))} aria-label={`Select ${r.description}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-ink">{r.description}</p>
                  <p className="text-[11px] text-faint">{r.date} · {r.category} · {r.supplier}</p>
                </div>
                <span className="text-sm tabular-nums text-ink">{formatHumanCurrency(r.amount)}</span>
              </li>
            ))}
          </ul>
          <div className="max-w-xs">
            <Field label="Reconcile into sub-project (optional)"><Select value={subProjectId} onChange={(e) => setSubProjectId(e.target.value)}><option value="">Project level</option>{subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="rounded-md border border-line bg-subtle/40 p-4 text-sm">
          <p className="font-medium text-ink">{rows.length} rows · {formatHumanCurrency(rows.reduce((a, r) => a + r.amount, 0))}</p>
          <p className="mt-1 text-mute">These will be imported as confirmed, reconciled spend against this project's budget by category.</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[...new Set(rows.map((r) => r.category))].map((c) => <Pill key={c} variant="neutral">{c}</Pill>)}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        {step > 0 ? <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>Back</Button> : <span />}
        {step < 3 ? (
          <Button disabled={step === 2 && selected.length === 0} onClick={() => setStep((s) => s + 1)}>Continue</Button>
        ) : (
          <Button disabled={rows.length === 0} onClick={() => setConfirmImport(true)}>Import {rows.length} rows</Button>
        )}
      </div>

      <AlertDialog
        open={confirmImport}
        onOpenChange={setConfirmImport}
        title="Import as confirmed spend?"
        description={`This imports ${rows.length} statement row${rows.length === 1 ? "" : "s"} totalling ${formatHumanCurrency(rowsTotal)} as confirmed, reconciled spend against this project's budget. This cannot be undone.`}
        confirmLabel={`Import ${formatHumanCurrency(rowsTotal)}`}
        onConfirm={() => onImport(rows, subProjectId || undefined)}
      />
    </div>
  );
}
